import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', full_name: '', role: 'salesperson' });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editData, setEditData] = useState({
    full_name: '',
    first_name: '',
    last_name: '',
    position_title: '',
    direct_phone: '',
    dealership_name_override: '',
    signature_block: '',
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const data = await api.getStaff();
      setStaff(data);
    } catch (error) {
      console.error('Failed to load staff:', error);
      setMessage({ type: 'error', text: 'Failed to load staff members' });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.updateStaffRole(userId, newRole);
      setMessage({ type: 'success', text: 'Role updated successfully' });
      loadStaff();
    } catch (error) {
      console.error('Failed to update role:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update role' });
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) {
      return;
    }

    try {
      await api.removeStaff(userId);
      setMessage({ type: 'success', text: 'Staff member removed' });
      loadStaff();
    } catch (error) {
      console.error('Failed to remove:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to remove staff member' });
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    try {
      const res = await api.inviteStaff(inviteData.email, inviteData.full_name, inviteData.role);
      const inviteUrl = res?.invite_url;
      const action = res?.action;
      const msg = res?.message || (
        action === 'password_reset_sent'
          ? `Password reset email sent to ${inviteData.email}.`
          : `Invite email sent to ${inviteData.email}.`
      );

      setMessage({ type: 'success', text: msg });

      // Keep a staging escape hatch: allow copying the link if backend returned it.
      if (inviteUrl) {
        window.prompt('Set-password link (copy/paste):', inviteUrl);
      }

      setShowInviteModal(false);
      setInviteData({ email: '', full_name: '', role: 'salesperson' });
      loadStaff();
    } catch (error) {
      console.error('Invite failed:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to send email' });
    }
  };

  const openEditModal = (member) => {
    setEditUser(member);
    setEditData({
      full_name: member.full_name || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      position_title: member.position_title || '',
      direct_phone: member.direct_phone || '',
      dealership_name_override: member.dealership_name_override || '',
      signature_block: member.signature_block || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;

    try {
      await api.updateStaffProfile(editUser.id, editData);
      setMessage({ type: 'success', text: 'Staff profile updated' });
      setShowEditModal(false);
      setEditUser(null);
      loadStaff();
    } catch (error) {
      console.error('Update failed:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update staff profile' });
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: '#ef4444',
      manager: '#f59e0b',
      salesperson: 'var(--brand-primary, #3b82f6)',
    };
    return (
      <span style={{
        ...styles.roleBadge,
        backgroundColor: colors[role] || '#64748b',
      }}>
        {role}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 Staff Management</h1>
          <p style={styles.subtitle}>Manage your team members and their roles</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          style={styles.inviteButton}
        >
          ➕ Invite Staff
        </button>
      </div>

      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          color: message.type === 'success' ? '#86efac' : '#fca5a5',
        }}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading staff...</div>
      ) : staff.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>👤</div>
          <div style={styles.emptyText}>No staff members yet</div>
          <button
            onClick={() => setShowInviteModal(true)}
            style={styles.emptyButton}
          >
            Invite your first team member
          </button>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Staff Member</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Joined</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(member => (
                <tr key={member.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.memberInfo}>
                      <div style={styles.avatar}>
                        {member.full_name?.charAt(0) || member.email.charAt(0)}
                      </div>
                      <div>
                        <div style={styles.memberName}>
                          {member.full_name || 'No name'}
                        </div>
                        <div style={styles.memberEmail}>{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.roleCell}>
                      {getRoleBadge(member.role)}
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value)}
                        style={styles.roleSelect}
                      >
                        <option value="salesperson">Salesperson</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.date}>{formatDate(member.created_at)}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openEditModal(member)}
                        style={styles.editButton}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRemove(member.id)}
                        style={styles.removeButton}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={styles.modalOverlay} onClick={() => setShowInviteModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Invite Staff Member</h2>
            
            <form onSubmit={handleInvite}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  required
                  style={styles.input}
                  placeholder="colleague@company.com"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name *</label>
                <input
                  type="text"
                  value={inviteData.full_name}
                  onChange={(e) => setInviteData({ ...inviteData, full_name: e.target.value })}
                  required
                  style={styles.input}
                  placeholder="John Smith"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                  style={styles.input}
                >
                  <option value="salesperson">Salesperson</option>
                  <option value="manager">Manager</option>
                </select>
              </div>

              <div style={styles.modalButtons}>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editUser && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Staff Profile</h2>

            <form onSubmit={handleSaveEdit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  value={editData.full_name}
                  onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>First Name</label>
                  <input
                    type="text"
                    value={editData.first_name}
                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    type="text"
                    value={editData.last_name}
                    onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Position / Title</label>
                <input
                  type="text"
                  value={editData.position_title}
                  onChange={(e) => setEditData({ ...editData, position_title: e.target.value })}
                  style={styles.input}
                  placeholder="Sales Consultant"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Direct Phone</label>
                <input
                  type="text"
                  value={editData.direct_phone}
                  onChange={(e) => setEditData({ ...editData, direct_phone: e.target.value })}
                  style={styles.input}
                  placeholder="+61..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Dealership Name Override</label>
                <input
                  type="text"
                  value={editData.dealership_name_override}
                  onChange={(e) => setEditData({ ...editData, dealership_name_override: e.target.value })}
                  style={styles.input}
                  placeholder="(optional)"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Signature Block Override</label>
                <textarea
                  value={editData.signature_block}
                  onChange={(e) => setEditData({ ...editData, signature_block: e.target.value })}
                  style={{ ...styles.input, minHeight: '90px', resize: 'vertical' }}
                  placeholder="(optional) If set, this replaces the generated signature"
                />
              </div>

              <div style={styles.modalButtons}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '900px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: 0,
    fontSize: '14px',
  },
  inviteButton: {
    padding: '10px 20px',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
    textAlign: 'center',
    padding: '48px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 32px',
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '18px',
    marginBottom: '16px',
  },
  emptyButton: {
    padding: '12px 24px',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  tableContainer: {
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '16px',
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #334155',
    background: '#0f172a',
  },
  tr: {
    borderBottom: '1px solid #334155',
  },
  td: {
    padding: '16px',
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'var(--brand-primary, #3b82f6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
  },
  memberName: {
    color: '#f1f5f9',
    fontSize: '14px',
    fontWeight: '500',
  },
  memberEmail: {
    color: '#64748b',
    fontSize: '12px',
  },
  roleCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  roleBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#fff',
    textTransform: 'uppercase',
  },
  roleSelect: {
    padding: '6px 8px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '4px',
    color: '#f1f5f9',
    fontSize: '13px',
  },
  date: {
    color: '#94a3b8',
    fontSize: '13px',
  },
  editButton: {
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: '4px',
    color: '#e2e8f0',
    fontSize: '12px',
    cursor: 'pointer',
    opacity: 0.9,
  },
  removeButton: {
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #ef4444',
    borderRadius: '4px',
    color: '#ef4444',
    fontSize: '12px',
    cursor: 'pointer',
    opacity: 0.8,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid #334155',
  },
  modalTitle: {
    color: '#f1f5f9',
    margin: '0 0 24px 0',
    fontSize: '20px',
    fontWeight: '600',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    color: '#94a3b8',
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    background: 'var(--brand-primary, #3b82f6)',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default StaffManagement;
